import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    LuPackage,
    LuBot,
    LuPlug,
    LuClock,
    LuUserPlus,
    LuUsers,
    LuTarget,
    LuTag,
    LuCheck,
    LuChevronRight,
    LuCircleAlert,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import { TITULO_FASE, type FasePasso } from '../config/primeirosPassos';
import { usePrimeirosPassos } from '../contexts/PrimeirosPassosContext';
import type { PassoId } from '../services/OnboardingStatusService';
import './OnboardingStepsPage.css';

/* ─────────────────────────────────────────────────────────────────────────
   PRIMEIROS PASSOS — Direção A ("a lista que sabe"), escolhida em 26/09.
   Proposta: docs/designsystem/propostas/primeiros-passos.html

   A tela anterior eram três cartões fixos, sem nenhuma chamada de API: dizia
   "cadastre seus produtos" para quem já tinha produtos, agente e três canais
   no ar. Aqui cada linha é uma leitura, e mostra a evidência do que existe.

   A lista de passos e a medição moram fora daqui (`config/primeirosPassos.ts`
   e `contexts/PrimeirosPassosContext.tsx`) porque o contador da sidebar usa
   as duas coisas — ver o comentário do contexto.
   ───────────────────────────────────────────────────────────────────────── */

const ICONE: Record<PassoId, ReactNode> = {
    produtos: <LuPackage size={14} />,
    agente: <LuBot size={14} />,
    canais: <LuPlug size={14} />,
    horario: <LuClock size={14} />,
    usuarios: <LuUserPlus size={14} />,
    equipes: <LuUsers size={14} />,
    metas: <LuTarget size={14} />,
    motivos: <LuTag size={14} />,
};

const FASES: FasePasso[] = ['atender', 'time'];

const OnboardingStepsPage = () => {
    const navigate = useNavigate();
    const { visiveis, sinais, carregando, concluidos, total, remedir } = usePrimeirosPassos();

    /* Mede de novo a cada visita: quem sai daqui para cadastrar um produto e
       volta tem que ver a linha mudar. O contexto ignora a chamada se já
       estiver medindo, então a primeira carga não mede duas vezes. */
    useEffect(() => { remedir(); }, [remedir]);

    const tudoPronto = total > 0 && concluidos === total;

    /* Uma ação primária por tela: a do primeiro passo pendente. */
    const primeiroPendente = carregando
        ? undefined
        : visiveis.find(p => !sinais[p.id]?.feito)?.id;

    const subtitulo = total === 0
        ? 'Nenhum destes ajustes está no seu acesso'
        : carregando
            ? 'Conferindo o que já está configurado…'
            : tudoPronto
                ? 'Tudo configurado. Esta tela fica aqui para conferir e ajustar.'
                : `${total === 1 ? 'Um ajuste' : `${total} ajustes`} para a Brik atender por você.`;

    return (
        <div className="page-shell pp-screen">
            <PageHeader centered title="Primeiros passos" subtitle={subtitulo} />

            <div className="page-body">
                <div className="pp-coluna">
                    {total > 0 && (
                        <div className="pp-progresso">
                            <div className="pp-trilho">
                                <i style={{ width: `${(concluidos / total) * 100}%` }} />
                            </div>
                            <span className="pp-contagem">
                                {carregando ? 'conferindo…' : `${concluidos} de ${total}`}
                            </span>
                        </div>
                    )}

                    {total === 0 && (
                        <div className="pp-vazio">
                            <LuCircleAlert size={16} />
                            <div>
                                <p className="pp-vazio-t">A configuração da conta é do administrador</p>
                                <p className="pp-vazio-s">
                                    Quem cuida de produtos, agente de IA, canais e metas é quem tem
                                    acesso a essas telas. Fale com o administrador da sua empresa se
                                    precisar mudar alguma coisa por aqui.
                                </p>
                            </div>
                        </div>
                    )}

                    {FASES.map(fase => {
                        const daFase = visiveis.filter(p => p.fase === fase);
                        if (daFase.length === 0) return null;

                        return (
                            <section key={fase} className="pp-fase">
                                <h2 className="pp-fase-t">{TITULO_FASE[fase]}</h2>
                                <div className="pp-lista">
                                    {daFase.map(passo => {
                                        const sinal = sinais[passo.id];
                                        const feito = !!sinal?.feito;

                                        return (
                                            <div
                                                key={passo.id}
                                                className={`pp-passo ${feito ? 'is-feito' : ''}`}
                                            >
                                                <span className={`pp-marca ${feito ? 'is-feito' : ''}`}>
                                                    {feito ? <LuCheck size={13} /> : ICONE[passo.id]}
                                                </span>

                                                <span className="pp-texto">
                                                    <span className="pp-titulo">{passo.titulo}</span>
                                                    <span
                                                        className={`pp-evidencia ${
                                                            !carregando && !feito ? 'is-falta' : ''
                                                        }`}
                                                    >
                                                        {carregando && !sinal
                                                            ? 'conferindo…'
                                                            : sinal?.detalhe ?? '—'}
                                                    </span>
                                                </span>

                                                {feito ? (
                                                    <button
                                                        type="button"
                                                        className="pp-btn is-fantasma"
                                                        onClick={() => navigate(passo.rota)}
                                                        aria-label={`Abrir ${passo.titulo}`}
                                                    >
                                                        <LuChevronRight size={16} />
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className={`pp-btn ${
                                                            passo.id === primeiroPendente
                                                                ? 'is-primario'
                                                                : 'is-secundario'
                                                        }`}
                                                        onClick={() => navigate(passo.rota)}
                                                        disabled={carregando && !sinal}
                                                    >
                                                        {passo.acao}
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default OnboardingStepsPage;
